import { MAX_DIFF } from '@/constants/question-type'
import { pickAdvanceTarget, pickRetreatTarget } from '@/utils/max-diff'

/**
 * Lets a MaxDiff question borrow the survey's own Next and Back buttons.
 *
 * A MaxDiff question asks the respondent to work through several screens, and every
 * other survey platform moves between those screens with the survey's ordinary Next
 * button rather than adding controls inside the question. Doing the same here means the
 * respondent meets no control they have not already used, which is the whole point:
 * nothing about the survey behaves differently because a MaxDiff is in it.
 *
 * The guards below are the only thing standing between that and the navigation path
 * every response of every survey travels through, so they are built to be inert. On a
 * page with no MaxDiff, maxDiffScreenList is empty, both pick* helpers return null, and
 * the guards return false before touching anything — the view carries on exactly as it
 * did. That is why the guard is a plain boolean check at the top of onSubmit/onBack
 * rather than a rewrite of them.
 *
 * Screen positions arrive by event rather than being read off $refs, because $refs are
 * not reactive: a label computed from a ref would never update when the screen changed.
 * The refs are still used to *command* a component to move, which needs no reactivity.
 */
export const MaxDiffPageNavMixin = {
  data () {
    return {
      // qid -> { qid, index, total }, reported by each MaxDiff component on this page.
      maxDiffScreens: {},
    }
  },

  computed: {
    /**
     * Screen positions of the MaxDiff questions on this page, in the order they appear.
     *
     * Driven off `questions` rather than off the reported state, so a position left over
     * from a page the respondent has moved on from cannot influence anything.
     */
    maxDiffScreenList () {
      return (this.questions || [])
        .filter(question => question && question.type === MAX_DIFF)
        .map(question => this.maxDiffScreens[question.qid])
        .filter(Boolean)
    },

    /**
     * True while a MaxDiff on this page still has screens ahead of it.
     *
     * Used to keep the button honest: it must read "Next" while pressing it moves to
     * another screen, and only become "Submit" once pressing it really does submit.
     */
    maxDiffHasNextScreen () {
      return pickAdvanceTarget(this.maxDiffScreenList) !== null
    },
  },

  methods: {
    onMaxDiffScreen (state) {
      if (!state || state.qid === undefined) return

      this.$set(this.maxDiffScreens, state.qid, state)
    },

    /**
     * The mounted component for a question id.
     *
     * Refs registered inside v-for are arrays, and the views differ in whether the
     * question list they render is looped, so both shapes are handled here rather than
     * at each call site.
     */
    maxDiffComponentFor (qid) {
      const question = (this.questions || []).find(item => item && item.qid === qid)
      if (!question) return null

      const ref = this.$refs[`ref${question.code}`]

      return Array.isArray(ref) ? ref[0] : ref || null
    },

    /**
     * Consume a press of Next, if a MaxDiff still has screens to show.
     *
     * @returns {boolean} true when the press was used to move a screen and the caller
     *                    must not submit the page
     */
    maxDiffAdvance () {
      const qid = pickAdvanceTarget(this.maxDiffScreenList)
      if (qid === null) return false

      const component = this.maxDiffComponentFor(qid)
      if (!component || typeof component.goToNextScreen !== 'function') return false

      return component.goToNextScreen()
    },

    /**
     * Consume a press of Back, if a MaxDiff has a screen behind it.
     *
     * @returns {boolean} true when the press was used to move a screen and the caller
     *                    must not leave the page
     */
    maxDiffRetreat () {
      const qid = pickRetreatTarget(this.maxDiffScreenList)
      if (qid === null) return false

      const component = this.maxDiffComponentFor(qid)
      if (!component || typeof component.goToPreviousScreen !== 'function') return false

      return component.goToPreviousScreen()
    },
  },
}
